import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Loader2, Package, XCircle, Paintbrush, ArrowRight } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const QuoteSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, loading: authLoading } = useCustomerAuth();
  const quoteId = searchParams.get('quote_id');
  const [status, setStatus] = useState('checking');
  const [quoteData, setQuoteData] = useState(null);

  useEffect(() => {
    if (!quoteId) { setStatus('error'); return undefined; }
    if (authLoading) return undefined;
    let stopped = false;
    let timer;
    const poll = async (attempt = 0) => {
      try {
        const res = await axios.get(`${API}/api/quotes/${quoteId}/payment-status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (stopped) return;
        setQuoteData(res.data);
        if (res.data.status === 'paid') { setStatus('success'); return; }
      } catch {
        if (stopped) return;
      }
      if (attempt >= 7) { setStatus('timeout'); return; }
      timer = setTimeout(() => poll(attempt + 1), 2000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [quoteId, token, authLoading]);

  const canOpen = quoteData?.portal_action === 'open';
  const handleGoToDesign = () => navigate(`/portal/orders/${quoteData.order_id}/design`);
  const handleGoToPortal = () => {
    if (quoteData?.portal_action === 'set_password') navigate('/portal/set-password');
    else if (canOpen) navigate('/portal/dashboard');
    else navigate('/portal/login?mode=setup');
  };

  return (
    <div className="min-h-screen bg-[#0A1C29] flex items-center justify-center p-4" data-testid="quote-success-page">
      <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] max-w-lg w-full p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-[#F5A623] mx-auto mb-4 animate-spin" />
            <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Verifying Payment</h2>
            <p className="text-sm text-[#A0B8C7]" style={{ fontFamily: 'Manrope' }}>Confirming your deposit payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#34C759]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#34C759]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }} data-testid="quote-success-title">
              Deposit Received!
            </h2>
            <p className="text-sm text-[#A0B8C7] mb-1" style={{ fontFamily: 'Manrope' }}>
              Quote #{quoteData?.quote_number}
              {quoteData?.order_number && <span> → Order <span className="text-[#F5A623] font-semibold">{quoteData.order_number}</span></span>}
            </p>
            {quoteData?.deposit && (
              <p className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
                ${quoteData.deposit.toFixed(2)} paid
              </p>
            )}

            {!canOpen && <p className="text-sm text-[#A0B8C7] my-6" data-testid="portal-access-notice">
              {quoteData?.portal_action === 'set_password'
                ? 'Choose your password to finish setting up your portal account.'
                : 'Sign in to view your order. First visit? Use the setup link in your email, or request an access link.'}
            </p>}

            {/* Action Buttons */}
            <div className="space-y-3">
              {canOpen && quoteData?.order_id && (
                <button onClick={handleGoToDesign} data-testid="go-to-design-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#F5A623] text-[#0A1C29] font-bold rounded-xl hover:bg-[#D88F1B] transition text-sm"
                  style={{ fontFamily: 'Outfit' }}>
                  <Paintbrush className="w-4 h-4" /> Start Designing <ArrowRight className="w-4 h-4" />
                </button>
              )}
              <button onClick={handleGoToPortal} data-testid="go-to-portal-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#1E4159] text-[#A0B8C7] font-medium rounded-xl hover:border-[#4A90A4] hover:text-white transition text-sm"
                style={{ fontFamily: 'Manrope' }}>
                <Package className="w-4 h-4" /> {canOpen ? 'View My Orders' : quoteData?.portal_action === 'set_password' ? 'Set My Password' : 'Sign In or Set Up Access'}
              </button>
              <p className="text-[10px] text-[#A0B8C7]/50" style={{ fontFamily: 'Manrope' }}>You'll receive a confirmation email shortly</p>
            </div>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              {status === 'timeout' ? 'Verification Timed Out' : 'Something Went Wrong'}
            </h2>
            <p className="text-sm text-[#A0B8C7] mb-6" style={{ fontFamily: 'Manrope' }}>Please check your email for a confirmation or contact us.</p>
            <Link to="/" className="text-[#F5A623] hover:underline text-sm font-medium">Back to Home</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default QuoteSuccessPage;
